import React, { useEffect, useState } from "react";
import { Dialog } from "@material-ui/core";
import { OrderStatus, OrderStatusLabelMap, PriceDetail } from "../../../../../../models/Order";
import {
	CancelNoRefundReason,
	CancelOrderByAdminWithRefund,
	CancelOrderByAdminWithoutRefund,
} from "../../../../../../services/OrderService";

/**
 * Admin "Cancel booking…" (Quân chốt 16/10/2026): one entry point, the refund decision is forced.
 * Backend refuses instead of silently cancelling when a refund cannot happen.
 */
type Choice = "refund" | "no_refund" | null;

const REASONS: { value: CancelNoRefundReason; label: string }[] = [
	{ value: "no_show", label: "No-Show (customer did not drop off)" },
	{ value: "late_cancellation", label: "Late cancellation (outside policy)" },
	{ value: "duplicate", label: "Duplicate booking" },
	{ value: "dispute_lost", label: "Dispute lost (customer got the money back via bank / PayPal)" },
	{ value: "other", label: "Other" },
];

const METHOD_LABEL: Record<number, string> = { 1: "OnePay", 2: "PayPal", 3: "Stripe" };

/** Statuses where money was paid, so the admin must decide refund / no refund. */
export const CANCELLABLE_PAID_STATUSES = [
	OrderStatus.Paid,
	OrderStatus.Reserved,
	OrderStatus.NoShow,
	OrderStatus.Finish,
];

export default function CancelBookingDialog(props: {
	open: boolean;
	order: PriceDetail;
	canRefund: boolean;
	/** Lý do khoá lựa chọn hoàn (vd. đã thua dispute) — có thì hiện thay câu "thiếu quyền". */
	refundBlockedReason?: string;
	/** Đơn đã thua dispute ⇒ mở sẵn "Cancel without refund" + lý do "Dispute lost" (17/10/2026). */
	disputeLost?: boolean;
	onClose: () => void;
	onDone: (message: string) => void;
}) {
	const [choice, setChoice] = useState<Choice>(null);
	const [reason, setReason] = useState<CancelNoRefundReason | "">("");
	const [note, setNote] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		if (props.open) {
			setChoice(props.disputeLost ? "no_refund" : null);
			setReason(props.disputeLost ? "dispute_lost" : "");
			setNote("");
			setBusy(false);
			setError("");
		}
	}, [props.open, props.disputeLost]);

	const o = props.order;
	const status = Number(o.status);
	const amount = (Math.round((o.totalMoney || 0) * 100) / 100).toFixed(2);
	const method = METHOD_LABEL[Number(o.paymentMethod)] || "—";
	const customer = (o as any).customer?.email || (o as any).user?.email || o.email || "—";
	const canSubmit =
		!busy &&
		((choice === "refund" && props.canRefund) ||
			(choice === "no_refund" && !!reason && (reason !== "other" || !!note.trim())));

	const submit = async () => {
		if (!canSubmit || !o.id) return;
		setBusy(true);
		setError("");
		try {
			if (choice === "refund") {
				const r = await CancelOrderByAdminWithRefund(o.id);
				props.onDone(
					r?.refundStatus === 2
						? `Booking #${o.id} cancelled. Refund of ${amount} USD is pending at ${method}.`
						: `Booking #${o.id} cancelled and ${amount} USD refunded via ${method}.`
				);
			} else {
				await CancelOrderByAdminWithoutRefund(o.id, reason as CancelNoRefundReason, note.trim() || undefined);
				props.onDone(`Booking #${o.id} cancelled without refund.`);
			}
		} catch (err: any) {
			setError(err?.response?.data?.message || err?.message || "Cancel failed — nothing was changed.");
			setBusy(false);
		}
	};

	const card = (active: boolean, tone: "danger" | "neutral", disabled = false): React.CSSProperties => ({
		display: "block",
		border: `2px solid ${active ? (tone === "danger" ? "#DC2626" : "#334155") : "#E5E7EB"}`,
		background: disabled ? "#F9FAFB" : active ? (tone === "danger" ? "#FEF2F2" : "#F8FAFC") : "#fff",
		borderRadius: 12,
		padding: "14px 16px",
		cursor: disabled ? "not-allowed" : "pointer",
		marginBottom: 10,
		opacity: disabled ? 0.6 : 1,
	});

	return (
		<Dialog
			open={props.open}
			onClose={busy ? undefined : props.onClose}
			maxWidth="sm"
			fullWidth>
			<div style={{ padding: 24, fontSize: 14, color: "#111827" }}>
				<div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Cancel booking #{o.id}</div>
				<div style={{ color: "#6B7280", marginBottom: 16 }}>
					This cannot be undone. Choose what happens to the customer's payment.
				</div>

				<div
					style={{
						display: "grid",
						gridTemplateColumns: "120px 1fr",
						rowGap: 6,
						padding: 12,
						borderRadius: 10,
						background: "#F9FAFB",
						marginBottom: 16,
					}}>
					<span style={{ color: "#6B7280" }}>Customer</span>
					<span style={{ wordBreak: "break-all" }}>{customer}</span>
					<span style={{ color: "#6B7280" }}>Station</span>
					<span>{o.partner?.displayName || o.partner?.name || "—"}</span>
					<span style={{ color: "#6B7280" }}>Current status</span>
					<span style={{ fontWeight: 600 }}>{OrderStatusLabelMap.get(status)}</span>
					<span style={{ color: "#6B7280" }}>Paid</span>
					<span style={{ fontWeight: 600 }}>
						{amount} USD · {method}
					</span>
				</div>

				{status === OrderStatus.NoShow && (
					<div style={noteBox("#EFF6FF", "#1E40AF")}>
						This booking is <b>No-Show</b>. Under the No-Show rule WhaleLO keeps the payment — refund only as an
						exception (e.g. the station was closed or refused the bags).
					</div>
				)}
				{status === OrderStatus.Finish && (
					<div style={noteBox("#FFFBEB", "#92400E")}>
						This booking is <b>Finished</b> — the station has served it. A refund is paid by WhaleLO. If the
						booking is already in a station payout that is in transit or paid, it cannot be cancelled.
					</div>
				)}

				<div style={{ fontWeight: 700, margin: "4px 0 10px" }}>Refund the customer?</div>

				<label style={card(choice === "refund", "danger", !props.canRefund)}>
					<input
						type="radio"
						name={`cancel-choice-${o.id}`}
						checked={choice === "refund"}
						disabled={!props.canRefund || busy}
						onChange={() => setChoice("refund")}
						style={{ marginRight: 8 }}
					/>
					<b>Cancel &amp; refund {amount} USD</b>
					<div style={{ color: "#4B5563", margin: "4px 0 0 24px" }}>
						{props.canRefund
							? `Full refund to the original payment method (${method}). The customer gets the “Booking Canceled” email with the refunded amount.`
							: props.refundBlockedReason || "You don't have the Refund permission (orders.refund)."}
					</div>
					<div style={resultLine}>
						Admin sees: <b>Cancelled · Refunded</b> &nbsp;|&nbsp; Station sees: <b>Cancelled</b>
					</div>
				</label>

				<label style={card(choice === "no_refund", "neutral")}>
					<input
						type="radio"
						name={`cancel-choice-${o.id}`}
						checked={choice === "no_refund"}
						disabled={busy}
						onChange={() => setChoice("no_refund")}
						style={{ marginRight: 8 }}
					/>
					<b>Cancel without refund</b>
					<div style={{ color: "#4B5563", margin: "4px 0 0 24px" }}>
						WhaleLO keeps the payment. The customer does <b>not</b> get an email.
					</div>
					{choice === "no_refund" && (
						<div style={{ margin: "10px 0 0 24px" }}>
							<select
								aria-label="Reason"
								value={reason}
								disabled={busy}
								onChange={(e) => setReason(e.target.value as CancelNoRefundReason)}
								style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #D1D5DB" }}>
								<option value="">Reason (required)…</option>
								{REASONS.map((r) => (
									<option
										key={r.value}
										value={r.value}>
										{r.label}
									</option>
								))}
							</select>
							{reason === "other" && (
								<input
									aria-label="Reason note"
									value={note}
									disabled={busy}
									maxLength={500}
									onChange={(e) => setNote(e.target.value)}
									placeholder="Write the reason"
									style={{
										width: "100%",
										marginTop: 8,
										padding: 8,
										borderRadius: 8,
										border: "1px solid #D1D5DB",
										boxSizing: "border-box",
									}}
								/>
							)}
						</div>
					)}
					<div style={resultLine}>
						Admin sees: <b>Cancelled · No refund</b> &nbsp;|&nbsp; Station sees: <b>Cancelled</b>
					</div>
				</label>

				{error && (
					<div
						role="alert"
						style={noteBox("#FEF2F2", "#B91C1C")}>
						{error}
					</div>
				)}

				<div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
					<button
						type="button"
						onClick={props.onClose}
						disabled={busy}
						style={{
							padding: "10px 16px",
							borderRadius: 8,
							border: "1px solid #D1D5DB",
							background: "#fff",
							cursor: busy ? "not-allowed" : "pointer",
							fontWeight: 600,
						}}>
						Keep booking
					</button>
					<button
						type="button"
						disabled={!canSubmit}
						onClick={submit}
						style={{
							padding: "10px 16px",
							borderRadius: 8,
							border: "none",
							fontWeight: 700,
							cursor: canSubmit ? "pointer" : "not-allowed",
							color: "#fff",
							background: !canSubmit ? "#9CA3AF" : choice === "refund" ? "#DC2626" : "#334155",
						}}>
						{busy
							? "Working…"
							: choice === "refund"
							? `Cancel & refund ${amount} USD`
							: choice === "no_refund"
							? "Cancel without refund"
							: "Choose an option"}
					</button>
				</div>
			</div>
		</Dialog>
	);
}

const resultLine: React.CSSProperties = { color: "#6B7280", fontSize: 12, margin: "8px 0 0 24px" };

function noteBox(bg: string, color: string): React.CSSProperties {
	return { background: bg, color, borderRadius: 10, padding: "10px 12px", marginBottom: 14, lineHeight: 1.45 };
}
